import { supabase } from '@/integrations/supabase/client';

/**
 * Descobre DE QUEM é um telefone, para o softphone mostrar o nome em vez de só
 * o número.
 *
 * Douglas 06/08, no meio de uma ligação: "apertei para ligar e agora não sei
 * qual eu tinha ligado". O softphone já sabia exibir nome (`contactName`), mas
 * nenhum dos 33 pontos de "Ligar" do CRM passava esse dado. Em vez de mexer nos
 * 33, a busca acontece aqui, uma vez, pelo próprio número.
 *
 * Procura do mais provável para o menos: lead (é de onde sai a ligação
 * comercial) → cliente da carteira → contato do WhatsApp. Não achou, devolve
 * null e o painel mostra o número, como antes — nunca inventa nome.
 */

type Consulta = {
  or: (expr: string) => Consulta;
  limit: (n: number) => Consulta;
  order: (col: string, opts: { ascending: boolean }) => Consulta;
};

/** O generic Database estoura a profundidade do compilador; a forma é declarada aqui. */
const tabela = (nome: string, colunas: string) =>
  (supabase as unknown as {
    from: (t: string) => { select: (c: string) => Consulta & Promise<{ data: Record<string, string | null>[] | null }> };
  }).from(nome).select(colunas);

/** Últimos 8 dígitos — sobrevive a DDI, DDD e ao nono dígito entrando ou saindo */
function sufixo(telefone: string): string | null {
  const d = String(telefone || '').replace(/\D/g, '');
  return d.length >= 8 ? d.slice(-8) : null;
}

function primeiroTextoUtil(linha: Record<string, string | null> | undefined, campos: string[]): string | null {
  if (!linha) return null;
  for (const campo of campos) {
    const v = String(linha[campo] ?? '').trim();
    if (v) return v;
  }
  return null;
}

export async function identificarNumero(telefone: string): Promise<string | null> {
  const s = sufixo(telefone);
  if (!s) return null;

  try {
    const { data: leads } = await tabela(
      'leads_carteira',
      'razao_social, nome_fantasia, contato_real_nome, telefone, contato_real_telefone, telefone_adm',
    ).or(
      `telefone.ilike.%${s}%,contato_real_telefone.ilike.%${s}%,telefone_adm.ilike.%${s}%`,
    ).limit(1);

    const lead = leads?.[0];
    if (lead) {
      const empresa = primeiroTextoUtil(lead, ['nome_fantasia', 'razao_social']);
      const pessoa = primeiroTextoUtil(lead, ['contato_real_nome']);
      if (empresa && pessoa) return `${pessoa} · ${empresa}`;
      if (empresa || pessoa) return (empresa || pessoa) as string;
    }
  } catch { /* segue para a próxima fonte */ }

  try {
    const { data: clientes } = await tabela(
      'clientes',
      'razao_social, nome_fantasia, telefone_principal, whatsapp',
    ).or(`telefone_principal.ilike.%${s}%,whatsapp.ilike.%${s}%`).limit(1);

    const nome = primeiroTextoUtil(clientes?.[0], ['nome_fantasia', 'razao_social']);
    if (nome) return nome;
  } catch { /* segue */ }

  try {
    const { data: conversas } = await tabela(
      'whatsapp_conversations',
      'contact_name, telefone, telefone_canonico',
    ).or(`telefone.ilike.%${s}%,telefone_canonico.ilike.%${s}%`).limit(1);

    const nome = primeiroTextoUtil(conversas?.[0], ['contact_name']);
    if (nome) return nome;
  } catch { /* segue */ }

  return null;
}
